import {
  API_ENDPOINT,
  LINKEDIN_CLIENT_ID,
  LINKEDIN_CLIENT_SECRET,
  LINKEDIN_OAUTH_URL,
  LINKEDIN_SCOPE,
  LINKEDIN_STATE,
} from "@/constants/appConstants";
import { useNetwork } from "@/hooks/useNetwork";
import { UserProfile } from "@/types/userDetails";
import { useEffect, useRef, useState } from "react";

const useLinkedInAuthentication = () => {
  const { fetchData: fetchAPI } = useNetwork();
  const [isLinkedInLoading, setIsLinkedInLoading] = useState(false);
  const [linkedInUser, setLinkedInUser] = useState<UserProfile | null>(null);
  const openedWindowRef = useRef<Window | null>(null);
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch LinkedIn user's email using the access token
  const fetchLinkedInEmail = async (accessToken: string) => {
    try {
      const response = await fetch("/seeker-profile/api/fetchLinkedinEmail", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ accessToken }),
      });
      const data = await response.json();
      return data.email || null;
    } catch (e) {
      console.error("Failed to fetch LinkedIn email via backend:", e);
      return null;
    }
  };

  const requestAccessToken = async (code: string) => {
    setIsLinkedInLoading(true);
    const hostname = window.location.hostname;
    const linkedInRedirectUrl = `${window.location.origin}/srp/linkedin-auth/`;
    const params = new URLSearchParams({
      grant_type: "authorization_code",
      code,
      client_id: LINKEDIN_CLIENT_ID,
      client_secret: LINKEDIN_CLIENT_SECRET,
      redirect_uri: linkedInRedirectUrl,
    });
    try {
      const addDetailsResponse = await fetchAPI(
        `${API_ENDPOINT(hostname).middleware.linkedinAuth}?${params.toString()}`,
        "POST",
        {
          "Content-Type": "application/x-www-form-urlencoded",
          "cache-control": "no-cache",
          "X-Language-Code": "en",
          "X-Source-Site-Context": "rexMonster",
        },
      );
      if (addDetailsResponse?.data?.access_token) {
        const accessToken = addDetailsResponse.data.access_token;
        const email = await fetchLinkedInEmail(accessToken);
        setLinkedInUser({
          accessToken,
          provider: "LinkedIn",
          email,
        });
      } else {
        setLinkedInUser(null);
      }
    } finally {
      setIsLinkedInLoading(false);
    }
  };

  const handleMessage = async (event: MessageEvent) => {
    // Only process messages from the same origin
    if (event.origin !== window.location.origin) {
      return;
    }

    if (event.data && event.data.type === "code generated") {
      setIsLinkedInLoading(true);

      // Extract code from popup URL (popup should be on same origin now)
      try {
        if (openedWindowRef.current && !openedWindowRef.current.closed) {
          const popupUrl = openedWindowRef.current.location.href;
          const url = new URL(popupUrl);
          const code = url.searchParams.get("code");
          const error = url.searchParams.get("error_description");

          if (code) {
            await requestAccessToken(code);
          } else if (error) {
            setIsLinkedInLoading(false);
          }
        }
      } catch (e) {
        console.error("Error extracting code from popup:", e);
        setIsLinkedInLoading(false);
      }

      if (openedWindowRef.current) {
        openedWindowRef.current.close();
      }
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      setIsLinkedInLoading(false);
    }
  };

  const pollPopup = () => {
    if (!openedWindowRef.current) {
      return;
    }

    try {
      // Check if popup is closed
      if (openedWindowRef.current.closed) {
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
        setIsLinkedInLoading(false);
        return;
      }

      // Try to access the popup's location (only works when on same origin)
      const popupUrl = openedWindowRef.current.location.href;
      const callbackUrl = `${window.location.origin}/srp/linkedin-auth/`;

      if (popupUrl.startsWith(callbackUrl)) {
        // Popup has redirected to callback URL, extract code
        const url = new URL(popupUrl);
        const code = url.searchParams.get("code");
        const error = url.searchParams.get("error_description");

        if (code) {
          setIsLinkedInLoading(true);
          requestAccessToken(code).then(() => {
            if (openedWindowRef.current) {
              openedWindowRef.current.close();
            }
          });
        } else if (error) {
          setIsLinkedInLoading(false);
          if (openedWindowRef.current) {
            openedWindowRef.current.close();
          }
        }

        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      }
    } catch (e) {
      // Cross-origin error - popup is still on LinkedIn's domain
      // This is expected, continue polling
    }
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, []);

  const handleLinkedInBtnClick = () => {
    const linkedInRedirectUrl = `${window.location.origin}/srp/linkedin-auth/`;
    const url = `${LINKEDIN_OAUTH_URL}&client_id=${LINKEDIN_CLIENT_ID}&scope=${LINKEDIN_SCOPE}&state=${LINKEDIN_STATE}&redirect_uri=${encodeURIComponent(linkedInRedirectUrl)}`;
    const width = 450,
      height = 730,
      left = window.screen.width / 2 - width / 2,
      top = window.screen.height / 2 - height / 2;

    setIsLinkedInLoading(true);

    const popup = window.open(
      url,
      "Linkedin",
      "menubar=no,location=no,resizable=no,scrollbars=no,status=no, width=" +
        width +
        ", height=" +
        height +
        ", top=" +
        top +
        ", left=" +
        left,
    );

    if (!popup) {
      setIsLinkedInLoading(false);
      console.error("Popup blocked. Please allow popups for this site.");
      return;
    }

    openedWindowRef.current = popup;

    // Start polling the popup window
    pollTimerRef.current = setInterval(pollPopup, 500);

    // Cleanup after 5 minutes (timeout)
    setTimeout(() => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (openedWindowRef.current && !openedWindowRef.current.closed) {
        openedWindowRef.current.close();
      }
      setIsLinkedInLoading(false);
    }, 300000); // 5 minutes timeout
  };

  return {
    isLinkedInLoading,
    handleLinkedInBtnClick,
    linkedInUser,
  };
};

export default useLinkedInAuthentication;
